import { Dsn, urlEncode } from '@sentry/utils';
var SENTRY_API_VERSION = '7';
/**
 * Helper class to provide urls, headers and metadata that can be used to form
 * different types of requests to Sentry endpoints.
 * Supports both envelopes and regular event requests.
 **/
var API = /** @class */function () {
  /** Create a new instance of API */
  function API(dsn, metadata, tunnel) {
    if (metadata === void 0) {
      metadata = {};
    }
    this.dsn = dsn;
    this._dsnObject = new Dsn(dsn);
    this.metadata = metadata;
    this._tunnel = tunnel;
  }
  /** Returns the Dsn object. */
  API.prototype.getDsn = function () {
    return this._dsnObject;
  };
  /** Does this transport force envelopes? */
  API.prototype.forceEnvelope = function () {
    return !!this._tunnel;
  };
  /** Returns the prefix to construct Sentry ingestion API endpoints. */
  API.prototype.getBaseApiEndpoint = function () {
    var dsn = this.getDsn();
    var protocol = dsn.protocol ? dsn.protocol + ":" : '';
    var port = dsn.port ? ":" + dsn.port : '';
    return protocol + "//" + dsn.host + port + (dsn.path ? "/" + dsn.path : '') + "/api/";
  };
  /** Returns the store endpoint URL. */
  API.prototype.getStoreEndpoint = function () {
    return this._getIngestEndpoint('store');
  };
  /**
   * Returns the store endpoint URL with auth in the query string.
   *
   * Sending auth as part of the query string and not as custom HTTP headers avoids CORS preflight requests.
   */
  API.prototype.getStoreEndpointWithUrlEncodedAuth = function () {
    return this.getStoreEndpoint() + "?" + this._encodedAuth();
  };
  /**
   * Returns the envelope endpoint URL with auth in the query string.
   *
   * Sending auth as part of the query string and not as custom HTTP headers avoids CORS preflight requests.
   */
  API.prototype.getEnvelopeEndpointWithUrlEncodedAuth = function () {
    if (this.forceEnvelope()) {
      return this._tunnel;
    }
    return this._getEnvelopeEndpoint() + "?" + this._encodedAuth();
  };
  /** Returns only the path component for the store endpoint. */
  API.prototype.getStoreEndpointPath = function () {
    var dsn = this.getDsn();
    return (dsn.path ? "/" + dsn.path : '') + "/api/" + dsn.projectId + "/store/";
  };
  /**
   * Returns an object that can be used in request headers.
   * This is needed for node and the old /store endpoint in sentry
   */
  API.prototype.getRequestHeaders = function (clientName, clientVersion) {
    // CHANGE THIS to use metadata but keep clientName and clientVersion compatible
    var dsn = this.getDsn();
    var header = ["Sentry sentry_version=" + SENTRY_API_VERSION];
    header.push("sentry_client=" + clientName + "/" + clientVersion);
    header.push("sentry_key=" + dsn.publicKey);
    if (dsn.pass) {
      header.push("sentry_secret=" + dsn.pass);
    }
    return {
      'Content-Type': 'application/json',
      'X-Sentry-Auth': header.join(', ')
    };
  };
  /** Returns the url to the report dialog endpoint. */
  API.prototype.getReportDialogEndpoint = function (dialogOptions) {
    if (dialogOptions === void 0) {
      dialogOptions = {};
    }
    var dsn = this.getDsn();
    var endpoint = this.getBaseApiEndpoint() + "embed/error-page/";
    var encodedOptions = [];
    encodedOptions.push("dsn=" + dsn.toString());
    for (var key in dialogOptions) {
      if (key === 'dsn') {
        continue;
      }
      if (key === 'user') {
        if (!dialogOptions.user) {
          continue;
        }
        if (dialogOptions.user.name) {
          encodedOptions.push("name=" + encodeURIComponent(dialogOptions.user.name));
        }
        if (dialogOptions.user.email) {
          encodedOptions.push("email=" + encodeURIComponent(dialogOptions.user.email));
        }
      } else {
        encodedOptions.push(encodeURIComponent(key) + "=" + encodeURIComponent(dialogOptions[key]));
      }
    }
    if (encodedOptions.length) {
      return endpoint + "?" + encodedOptions.join('&');
    }
    return endpoint;
  };
  /** Returns the envelope endpoint URL. */
  API.prototype._getEnvelopeEndpoint = function () {
    return this._getIngestEndpoint('envelope');
  };
  /** Returns the ingest API endpoint for target. */
  API.prototype._getIngestEndpoint = function (target) {
    if (this._tunnel) {
      return this._tunnel;
    }
    var base = this.getBaseApiEndpoint();
    var dsn = this.getDsn();
    return "" + base + dsn.projectId + "/" + target + "/";
  };
  /** Returns a URL-encoded string with auth config suitable for a query string. */
  API.prototype._encodedAuth = function () {
    var dsn = this.getDsn();
    var auth = {
      // We send only the minimum set of required information. See
      // https://github.com/getsentry/sentry-javascript/issues/2572.
      sentry_key: dsn.publicKey,
      sentry_version: SENTRY_API_VERSION
    };
    return urlEncode(auth);
  };
  return API;
}();
export { API };